/**
 * Реферальная программа.
 *
 * Пригласивший получает долю с каждой оплаты приглашённого. Начисление
 * сначала «на удержании» — пока идёт окно возврата, — потом становится
 * доступным к выводу. Обе суммы показываются явно: иначе «заработано
 * 1 000», а вывести можно 400, и это выглядит как обман.
 *
 * Вывод — в USDT на кошелёк. Сумма резервируется сразу при заявке,
 * перевод делает администратор.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { useReferral } from '@/entities/subscription/queries';
import {
  payoutErrorText,
  usePayoutRequest,
  type Network,
} from '@/features/referral/usePayoutRequest';
import type { Referral, ReferralPayout } from '@/shared/api/schemas';
import { config } from '@/shared/config/env';
import { formatPeriod, formatRub, formatShortDate } from '@/shared/lib/format';
import { shareLink } from '@/shared/lib/links';
import { useBackButton } from '@/shared/lib/useBackButton';
import { Screen } from '@/shared/ui/Screen';
import { Button } from '@/shared/ui/kit/Button';
import { Card, GroupLink } from '@/shared/ui/kit/Card';
import { Hero } from '@/shared/ui/kit/Hero';
import { Icon } from '@/shared/ui/kit/Icon';
import { Row } from '@/shared/ui/kit/Row';
import { Sheet } from '@/shared/ui/kit/Sheet';
import { Stat, StatGrid } from '@/shared/ui/kit/Stat';

import styles from './ReferralPage.module.css';

/** Сколько строк показывать на самой странице; остальное — в шторке. */
const PREVIEW = 3;

const SHARE_TEXT = 'Подключайся к ANROYAL VPN по моей ссылке';

const NETWORKS: readonly { id: Network; title: string; hint: string; placeholder: string }[] = [
  { id: 'usdt_trc20', title: 'TRC20', hint: 'сеть Tron', placeholder: 'T…' },
  { id: 'usdt_ton', title: 'TON', hint: 'сеть TON', placeholder: 'UQ… или EQ…' },
];

const PAYOUT_STATUS: Record<ReferralPayout['status'], string> = {
  requested: 'В обработке',
  completed: 'Выплачено',
  rejected: 'Отклонено',
};

/** «3 месяца», а не «90 дней»: так тариф назван на экране тарифов. */
function periodByDays(days: number): string {
  const months = days >= 360 ? 12 : Math.round(days / 30);
  const code = { 1: '1m', 3: '3m', 6: '6m', 12: '1y' }[months] ?? '';
  return formatPeriod(code, days);
}

function botLink(code: string): string {
  return `https://t.me/${config.botUsername}?start=ref_${code}`;
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    const done = () => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    };

    // Запасной путь нужен: clipboard недоступен вне защищённого контекста
    // и в части встроенных браузеров.
    const fallback = () => {
      const field = document.createElement('textarea');
      field.value = value;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.opacity = '0';
      document.body.append(field);
      field.select();
      document.execCommand('copy');
      field.remove();
      done();
    };

    if (navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(value).then(done, fallback);
      return;
    }
    fallback();
  };

  return (
    <Button variant="primary" block onClick={copy}>
      <Icon name={copied ? 'check' : 'copy'} size={18} />
      {copied ? 'Скопировано' : 'Скопировать ссылку'}
    </Button>
  );
}

/** «10% с каждой оплаты» или, у особых условий, «30% с первой подписки, 10% дальше». */
function rateText(data: Referral): string {
  const pct = (value: number) => `${value.toLocaleString('ru-RU', { maximumFractionDigits: 2 })}%`;
  if (data.first_rate_percent === data.rate_percent) return `${pct(data.rate_percent)} с каждой оплаты друзей`;
  return `${pct(data.first_rate_percent)} с первой подписки друга, ${pct(data.rate_percent)} с остальных оплат`;
}

function AccrualRows({ data, limit }: { data: Referral; limit: number }) {
  if (data.accruals.length === 0) {
    return (
      <Card>
        <p className={styles.empty}>
          Здесь появятся оплаты ваших друзей — {rateText(data)}
        </p>
      </Card>
    );
  }
  const now = Date.now();
  return (
    <Card>
      {data.accruals.slice(0, limit).map((item) => {
        const held = new Date(item.available_at).getTime() > now;
        const what = item.duration_days ? `VPN, ${periodByDays(item.duration_days)}` : 'VPN';
        return (
          <Row
            key={item.id}
            icon="users"
            title={item.name ?? 'Друг'}
            subtitle={
              item.reversed
                ? `${what} · возврат`
                : held
                  ? `${what} · доступно с ${formatShortDate(item.available_at)}`
                  : what
            }
            value={
              <span className={item.reversed ? styles.reversed : held ? styles.held : styles.money}>
                +{formatRub(item.amount_minor)}
              </span>
            }
          />
        );
      })}
    </Card>
  );
}

function PayoutRows({ payouts, limit }: { payouts: ReferralPayout[]; limit: number }) {
  if (payouts.length === 0) {
    return (
      <Card>
        <p className={styles.empty}>Выводов пока не было</p>
      </Card>
    );
  }
  return (
    <Card>
      {payouts.slice(0, limit).map((item) => (
        <Row
          key={item.id}
          icon="arrowUp"
          title={formatRub(item.amount_minor)}
          subtitle={`${PAYOUT_STATUS[item.status]} · ${formatShortDate(item.created_at)}${
            item.paid_amount ? ` · ${item.paid_amount}` : ''
          }${item.reject_reason ? ` · ${item.reject_reason}` : ''}`}
          value={item.network === 'usdt_ton' ? 'TON' : 'TRC20'}
        />
      ))}
    </Card>
  );
}

function PayoutForm({ data, onDone }: { data: Referral; onDone: () => void }) {
  const request = usePayoutRequest();
  const [network, setNetwork] = useState<Network>('usdt_trc20');
  const [address, setAddress] = useState('');
  const [amount, setAmount] = useState(String(Math.floor(data.available_minor / 100)));

  const amountMinor = Math.round(Number(amount.replace(',', '.')) * 100);
  const valid =
    Number.isFinite(amountMinor) &&
    amountMinor >= data.min_payout_minor &&
    amountMinor <= data.available_minor &&
    address.trim().length > 20;

  const submit = () => {
    request.mutate(
      { amount_minor: amountMinor, network, address: address.trim() },
      { onSuccess: onDone },
    );
  };

  const net = NETWORKS.find((n) => n.id === network) ?? NETWORKS[0];

  return (
    <div className={styles.form}>
      <label className={styles.field}>
        <span className={styles.label}>Сумма, ₽</span>
        <input
          className={styles.input}
          inputMode="decimal"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
        />
        <span className={styles.hint}>
          Доступно {formatRub(data.available_minor)} · минимум {formatRub(data.min_payout_minor)}
        </span>
      </label>

      <span className={styles.label}>Сеть USDT</span>
      <div className={styles.networks} role="radiogroup" aria-label="Сеть USDT">
        {NETWORKS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="radio"
            aria-checked={item.id === network}
            className={item.id === network ? `${styles.network} ${styles.active}` : styles.network}
            onClick={() => setNetwork(item.id)}
          >
            <b>{item.title}</b>
            <span>{item.hint}</span>
          </button>
        ))}
      </div>

      <label className={styles.field}>
        <span className={styles.label}>Адрес кошелька</span>
        <input
          className={`${styles.input} ${styles.mono}`}
          placeholder={net?.placeholder}
          autoComplete="off"
          spellCheck={false}
          value={address}
          onChange={(event) => setAddress(event.target.value)}
        />
        <span className={styles.hint}>
          Проверьте сеть: перевод на адрес другой сети не вернуть.
        </span>
      </label>

      <Button block disabled={!valid || request.isPending} onClick={submit}>
        {request.isPending ? 'Отправляем…' : 'Отправить заявку'}
      </Button>
      {request.error ? (
        <p className={styles.error} role="alert">
          {payoutErrorText(request.error)}
        </p>
      ) : (
        <p className={styles.hint}>
          Сумма спишется с баланса сразу, перевод в USDT по курсу на день выплаты — обычно в течение
          суток.
        </p>
      )}
    </div>
  );
}

type OpenSheet = 'accruals' | 'payouts' | 'withdraw' | null;

export function ReferralPage() {
  const navigate = useNavigate();
  const [sheet, setSheet] = useState<OpenSheet>(null);

  // Системная кнопка «назад» сначала закрывает шторку и только потом
  // уводит с экрана: иначе одно нажатие делает сразу два шага.
  useBackButton(() => {
    if (sheet !== null) {
      setSheet(null);
      return;
    }
    // Сюда приходят и из профиля, и с главной: возвращаемся туда, откуда пришли.
    void navigate(-1);
  });

  const { data, isLoading, error, refetch } = useReferral();
  const link = data?.code ? botLink(data.code) : '';
  const open = data?.payouts.find((payout) => payout.status === 'requested');
  const canWithdraw = data ? !open && data.available_minor >= data.min_payout_minor : false;

  return (
    <Screen isLoading={isLoading} error={error} onRetry={() => void refetch()}>
      {data ? (
        <>
          <Hero
            status={<span>Заработано</span>}
            value={formatRub(data.earned_minor)}
            until={rateText(data)}
          />

          <Card padded>
            <div className={styles.link}>{link || '—'}</div>
            <div className={styles.copyRow}>
              <CopyButton value={link} />
              <Button
                variant="quiet"
                onClick={() => void shareLink(link, SHARE_TEXT)}
                aria-label="Поделиться"
              >
                <Icon name="share" size={18} />
              </Button>
            </div>
          </Card>

          <StatGrid>
            <Stat icon="users" value={data.invited} label="Приглашено" />
            <Stat icon="wallet" value={formatRub(data.available_minor)} label="Доступно к выводу" />
          </StatGrid>

          {data.pending_minor > 0 ? (
            <p className={styles.note}>
              Ещё {formatRub(data.pending_minor)} на удержании: начисление становится доступным через{' '}
              {data.hold_days} дней после оплаты друга.
            </p>
          ) : null}

          {open ? (
            <Card>
              <Row
                icon="clock"
                title={`Заявка на ${formatRub(open.amount_minor)}`}
                subtitle="В обработке — переведём USDT в течение суток"
              />
            </Card>
          ) : (
            <Button block variant="quiet" disabled={!canWithdraw} onClick={() => setSheet('withdraw')}>
              <Icon name="wallet" size={18} />
              {canWithdraw
                ? 'Вывести в USDT'
                : `Вывод от ${formatRub(data.min_payout_minor)}`}
            </Button>
          )}

          <GroupLink onClick={() => setSheet('accruals')}>Начисления</GroupLink>
          <AccrualRows data={data} limit={PREVIEW} />

          <GroupLink onClick={() => setSheet('payouts')}>История выводов</GroupLink>
          <PayoutRows payouts={data.payouts} limit={PREVIEW} />

          <Sheet open={sheet === 'accruals'} title="Начисления" onClose={() => setSheet(null)}>
            <AccrualRows data={data} limit={data.accruals.length} />
          </Sheet>

          <Sheet open={sheet === 'payouts'} title="История выводов" onClose={() => setSheet(null)}>
            <PayoutRows payouts={data.payouts} limit={data.payouts.length} />
          </Sheet>

          <Sheet open={sheet === 'withdraw'} title="Вывод в USDT" onClose={() => setSheet(null)} fit>
            {sheet === 'withdraw' ? <PayoutForm data={data} onDone={() => setSheet(null)} /> : null}
          </Sheet>
        </>
      ) : null}
    </Screen>
  );
}
